import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import { useAPIError, useMode } from '../../context';
import {
    H1Title,
    ErrorBoundary,
    Introduction,
    ToggleMode,
} from '../../components/';
import { getAllProductReferences } from './service';
import MatchingTable from './MatchingTable';
import { statusMatching } from '../../utils/constants';

const Matching = ({ t }) => {
    const { addError } = useAPIError();
    const { mode } = useMode();

    const fetchIdRef = useRef(0);

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [dataMatching, setDataMatching] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [refresh, setRefresh] = useState(false);
    const [resetDebounce, setResetDebounce] = useState(false);
    const [filters, setFilters] = useState({
        statusFilter: '',
        productName: '',
        pageIndex: 0,
        pageSize: 25,
        activePageReset: false,
    });

    // to reset pageIndex, filters and debounce when needed to switch modes
    const resetAllFilters = (reset) => {
        if (reset) {
            setResetDebounce(true);

            setTimeout(() => {
                setResetDebounce(false);
            }, 1000);
        }

        document.getElementById('searchInput').value = '';
        setFilters({
            ...filters,
            statusFilter: '',
            productName: '',
            pageIndex: 0,
            pageSize: 25,
            activePageReset: true,
        });
    };

    /**
     *
     * @param {boolean} reset - to refresh data with resetted parameters or with current parameters
     */
    const handleRefreshData = (reset) => {
        if (reset) {
            resetAllFilters();
        } else {
            setRefresh(!refresh);
        }
    };

    const fetchData = useCallback(
        ({ debouncedFilters }) => {
            setIsLoading(true);
            let isEquivalence,
                isMonetary = null;

            if (debouncedFilters.statusFilter === statusMatching[2]) {
                isEquivalence = 0;
                isMonetary = 0;
            }
            if (debouncedFilters.statusFilter === statusMatching[1]) {
                isEquivalence = 1;
                isMonetary = null;
            }
            if (debouncedFilters.statusFilter === statusMatching[0]) {
                isEquivalence = 0;
                isMonetary = 1;
            }

            const fetchId = ++fetchIdRef.current;
            if (fetchId === fetchIdRef.current) {
                const params = {
                    limit: debouncedFilters.pageSize,
                    page: debouncedFilters.pageIndex + 1,
                    isMonetary,
                    isEquivalence,
                    archived: 0,
                    productName: debouncedFilters.productName || null,
                };

                getAllProductReferences(params, mode)
                    .then((res) => {
                        if (isMounted.current) {
                            setDataMatching(
                                res.data._embedded.productReferences,
                            );
                            setPageCount(res.data.page_count);
                            setTotalItems(res.data.total_items);
                        }
                    })
                    .catch((error) => {
                        if (error?.response?.data.status === 404) {
                            isMounted.current && setDataMatching([]);
                        } else {
                            addError(
                                error?.response?.data.detail,
                                error?.response?.data.status,
                            );
                        }
                    })
                    .finally(() => {
                        isMounted.current && setIsLoading(false);
                    });
            }
        },
        [addError, mode],
    );

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>
                    {t('matching.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <H1Title title={t('matching.title')}>
                <ToggleMode externalChange={() => resetAllFilters(true)} />
            </H1Title>

            <Introduction>{t('matching.introduction')}</Introduction>

            <ErrorBoundary>
                <MatchingTable
                    data={dataMatching}
                    fetchData={fetchData}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    pageCount={pageCount}
                    totalItems={totalItems}
                    filters={filters}
                    setFilters={setFilters}
                    handleRefreshData={handleRefreshData}
                    refresh={refresh}
                    resetDebounce={resetDebounce}
                />
            </ErrorBoundary>
        </>
    );
};

export default withTranslation('common')(Matching);
